import { CheckCircle, Leaf, Award, Users, Shield, Truck, Heart, Star } from 'lucide-react';
import { useScrollAnimation, useStaggeredAnimation } from '../../hooks/useScrollAnimation';

const benefits = [
  { icon: Leaf, title: '100% Natural & Chemical-Free', desc: 'Pure herbal formulations without harmful chemicals, steroids, or synthetic additives.', color: 'bg-green-50 text-green-600', num: '01' },
  { icon: Award, title: '60+ Years Herbal Experience', desc: 'Decades of traditional herbal wisdom passed down through generations since 1960.', color: 'bg-amber-50 text-amber-600', num: '02' },
  { icon: Users, title: 'Trusted by 1M+ Customers', desc: 'Millions of satisfied customers across Pakistan and internationally.', color: 'bg-blue-50 text-blue-600', num: '03' },
  { icon: Shield, title: 'Lab Tested & DRAP Approved', desc: 'All products undergo rigorous quality testing and are DRAP certified.', color: 'bg-purple-50 text-purple-600', num: '04' },
  { icon: Heart, title: 'Traditional Meets Modern Science', desc: 'Ancient herbal wisdom combined with modern scientific research for optimal effectiveness.', color: 'bg-red-50 text-red-500', num: '05' },
  { icon: Truck, title: 'Fast Delivery Across Pakistan', desc: '3–5 working days delivery with free shipping on orders above Rs. 3,000.', color: 'bg-cyan-50 text-cyan-600', num: '06' },
  { icon: Star, title: 'Proven Results & Reviews', desc: 'Thousands of verified testimonials and proven results for various health concerns.', color: 'bg-orange-50 text-orange-500', num: '07' },
  { icon: CheckCircle, title: 'Zero Side Effects', desc: 'Pure natural ingredients ensure zero harmful side effects when used as directed.', color: 'bg-emerald-50 text-emerald-600', num: '08' },
];

export function WhyChooseUs() {
  const { ref: headerRef, inView: headerInView } = useScrollAnimation();
  const { ref: gridRef, visibleItems } = useStaggeredAnimation(benefits.length, 80);

  return (
    <section className="py-24 bg-white relative overflow-hidden">
      {/* Decorative bg element */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-gray-50 to-transparent pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center mb-16 scroll-hidden ${headerInView ? 'scroll-visible' : ''}`}
        >
          <span className="section-tag justify-center">
            <CheckCircle className="w-4 h-4" />
            Why WellZora
          </span>
          <h2 className="section-heading text-center">
            Why Choose WellZora Herbal Care
          </h2>
          <p className="section-subheading mx-auto text-center">
            Discover what makes us Pakistan's most trusted herbal care brand
          </p>
        </div>

        {/* Benefits Grid */}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className={`group relative bg-white rounded-2xl p-6 border border-gray-100 hover:border-primary/30 hover:shadow-lg transition-all duration-400 cursor-default scroll-hidden ${visibleItems.includes(index) ? 'scroll-visible' : ''}`}
              style={{ transitionDelay: `${index * 50}ms` }}
            >
              {/* Large background number */}
              <span className="absolute top-4 right-5 text-6xl font-black text-gray-50 group-hover:text-primary/5 transition-colors duration-300 select-none leading-none">
                {benefit.num}
              </span>

              <div className={`relative w-12 h-12 ${benefit.color.split(' ')[0]} rounded-xl flex items-center justify-center mb-4`}>
                <benefit.icon className={`w-6 h-6 ${benefit.color.split(' ')[1]}`} />
              </div>

              <h3 className="relative font-semibold text-gray-900 mb-2 text-sm leading-snug">
                {benefit.title}
              </h3>
              <p className="relative text-gray-500 text-xs leading-relaxed">
                {benefit.desc}
              </p>

              {/* Bottom accent line on hover */}
              <div className="absolute bottom-0 left-6 right-6 h-0.5 bg-primary rounded-full scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}